import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Divider, Select,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUsers } from 'react-icons/fi';
import api from '../api';

export default function DepartmentsModal({ isOpen, onClose }) {
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#3182CE' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchDepts = async () => { try { const { data } = await api.get('/departments'); setDepartments(data); } catch(e) {} };
  const fetchUsers = async () => { try { const { data } = await api.get('/calendar/users'); setAllUsers(data); } catch(e) {} };

  const fetchDetail = async (id) => {
    try { const { data } = await api.get(`/departments/${id}`); setSelected(data); } catch(e) {}
  };

  useEffect(() => { if (isOpen) { fetchDepts(); fetchUsers(); setSelected(null); setShowForm(false); } }, [isOpen]);

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editingId) { await api.put(`/departments/${editingId}`, form); }
      else { await api.post('/departments', form); }
      await fetchDepts(); setShowForm(false); setEditingId(null);
      setForm({ name: '', description: '', color: '#3182CE' });
      toast({ title: editingId ? 'Updated' : 'Created', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const openEdit = (dept) => {
    setForm({ name: dept.name, description: dept.description || '', color: dept.color || '#3182CE' });
    setEditingId(dept.id); setShowForm(true);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            <HStack><FiUsers /><Text>Departments</Text></HStack>
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant={showForm ? 'solid' : 'outline'}
              onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ name: '', description: '', color: '#3182CE' }); }}>
              {showForm ? 'Cancel' : 'New'}
            </Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={3}><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                    <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Colour</FormLabel>
                    <Input size="sm" type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} /></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Textarea size="sm" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
                <Button size="xs" colorScheme="blue" onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                  {editingId ? 'Update' : 'Create'}</Button>
              </VStack>
            </Box>
          )}

          <HStack spacing={4} align="start">
            {/* Department List */}
            <VStack spacing={1} align="stretch" flex={1} minW="200px">
              {departments.map(d => (
                <Box key={d.id} p={2} border="1px" borderColor={selected?.id === d.id ? 'blue.400' : 'gray.100'}
                  borderRadius="md" cursor="pointer" bg={selected?.id === d.id ? 'blue.50' : 'white'}
                  _hover={{ bg: 'gray.50' }} onClick={() => fetchDetail(d.id)}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box w="10px" h="10px" borderRadius="full" bg={d.color || '#3182CE'} />
                      <Text fontSize="12px" fontWeight="600">{d.name}</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Badge fontSize="8px" colorScheme="gray">{d.user_count} users</Badge>
                      <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(d); }} aria-label="Edit" />
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={async (e) => { e.stopPropagation(); await api.delete(`/departments/${d.id}`); fetchDepts(); if (selected?.id === d.id) setSelected(null); }} aria-label="Delete" />
                    </HStack>
                  </Flex>
                  {d.description && <Text fontSize="10px" color="gray.500" mt={0.5}>{d.description}</Text>}
                </Box>
              ))}
              {departments.length === 0 && <Text fontSize="11px" color="gray.400" textAlign="center" py={4}>No departments</Text>}
            </VStack>

            {/* Detail Panel */}
            {selected && (
              <Box flex={1} minW="250px" p={3} border="1px" borderColor="gray.200" borderRadius="md">
                <Text fontSize="14px" fontWeight="700" mb={2}>{selected.name}</Text>
                {selected.description && <Text fontSize="11px" color="gray.500" mb={2}>{selected.description}</Text>}
                <Divider mb={2} />
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Members</Text>
                <Flex gap={1} flexWrap="wrap" mb={2}>
                  {(selected.users || []).map(u => (
                    <Badge key={u.user_id} colorScheme="blue" fontSize="10px" display="flex" alignItems="center" gap={1}>
                      {u.display_name || u.username}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        onClick={async () => { await api.delete(`/departments/${selected.id}/users/${u.user_id}`); fetchDetail(selected.id); fetchDepts(); }} aria-label="Remove" />
                    </Badge>
                  ))}
                </Flex>
                <Select size="xs" placeholder="Add user..." value=""
                  onChange={async (e) => { if (e.target.value) { await api.post(`/departments/${selected.id}/users`, { user_id: e.target.value }); fetchDetail(selected.id); fetchDepts(); } }}>
                  {allUsers.filter(u => !(selected.users || []).find(du => du.user_id === u.id)).map(u => (
                    <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                  ))}
                </Select>
              </Box>
            )}
          </HStack>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
